'use client'

import React from 'react'
import { motion } from 'framer-motion'
import { Button } from '@sarkiai/ui'
import { SarkiAIDetail as SarkiAIDetailComponent } from '@/components/home/SarkiAIDetail'
import { MessageSquare, Mic, Type, Image as ImageIcon, ArrowRight } from 'lucide-react'
import { useTranslations } from '@/components/I18nProvider'
import Link from 'next/link'

export default function SarkiAIPage() {
  const t = useTranslations('sarkiai_page')

  const capabilities = [
    { icon: MessageSquare, title: t('capabilities.c1_title'), desc: t('capabilities.c1_desc') },
    { icon: Mic, title: t('capabilities.c2_title'), desc: t('capabilities.c2_desc') },
    { icon: ImageIcon, title: t('capabilities.c3_title'), desc: t('capabilities.c3_desc') },
    { icon: Type, title: t('capabilities.c4_title'), desc: t('capabilities.c4_desc') },
  ]

  return (
    <div className="min-h-screen bg-background pt-20">
      {/* Detail Section as Hero */}
      <SarkiAIDetailComponent ctaLink="https://vcard.djotta.com/login" ctaLabelKey="sarkiai.start" />

      {/* Capabilities Grid */}
      <section className="py-24 md:py-32 bg-black text-white px-4 lg:px-12">
        <div className="container mx-auto">
          <div className="text-center mb-24 space-y-6">
            <h2 className="text-4xl md:text-5xl  uppercase tracking-tight text-white">{t('capabilities.title')}</h2>
            <p className="text-white/60 text-lg max-w-2xl mx-auto leading-relaxed">
              {t('capabilities.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
            {capabilities.map((c, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="p-10 rounded-[2.5rem] border border-white/10 bg-white/5 hover:bg-white/[0.07] hover:border-white/20 transition-all duration-500 group"
              >
                <div className="w-16 h-16 rounded-2xl bg-white text-black flex items-center justify-center mb-8 shadow-lg shadow-white/10 transition-transform group-hover:scale-110 shrink-0">
                  <c.icon className="w-8 h-8" />
                </div>
                <h3 className="text-xl  uppercase tracking-tight mb-4 text-white">{c.title}</h3>
                <p className="text-white/60 text-sm leading-relaxed">{c.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-24 md:py-32 bg-black dark:bg-white text-white dark:text-black overflow-hidden relative px-4 lg:px-12">
        <div className="container mx-auto text-center space-y-10 relative z-10 group">
          <h2 className="text-4xl md:text-5xl  uppercase tracking-tighter leading-none">
            {t('cta.title')}
          </h2>
          <p className="text-xl md:text-2xl opacity-80 max-w-3xl mx-auto leading-relaxed">
            {t('cta.subtitle')}
          </p>
          <div className="flex flex-wrap justify-center gap-6">
            <Button asChild size="default" className="rounded-2xl px-8 h-12 text-sm bg-white dark:bg-black text-black dark:text-white hover:bg-white/90 dark:hover:bg-black/90 font-bold uppercase tracking-widest transition-all shadow-xl">
              <Link href="https://vcard.djotta.com/login">
                {t('cta.start')}
                <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
            </Button>
            <Button asChild size="default" variant="outline" className="rounded-2xl px-8 h-12 text-sm border-white/40 dark:border-black/40 hover:bg-white/10 bg-black/10 dark:hover:bg-black/10 text-white dark:text-black font-bold uppercase tracking-widest transition-all">
              <Link href="/pricing">
                {t('cta.pricing')}
              </Link>
            </Button>
          </div>
        </div>

        {/* Decorative elements */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-white/5 dark:bg-black/5 rounded-full blur-[100px] -translate-y-1/2 translate-x-1/2" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-white/5 dark:bg-black/5 rounded-full blur-[100px] translate-y-1/2 -translate-x-1/2" />
      </section>
    </div>
  )
}
